import { Injectable, DestroyRef, effect, inject, signal, computed } from "@angular/core";
import { DEFAULT_LIST_PAGINATION, ListPagination, ListData, ListSearch, SearchSort, ListApiSearchRequest, ListApiSearchResponse } from "./risr-list-models";
import { Subject, distinctUntilChanged } from "rxjs";
import { logger } from "../logger";
import { toError } from "../utils/to-error";
import { HttpParams } from "@angular/common/http";
import { APP_BASE_HREF, Location } from "@angular/common";
import { ApiRaw } from "../api-raw";
import { RisrExpandableSchemaField } from "../forms/models/risr-schema-fields";

export interface ListStatus {
    status: 'loading' | 'resolved' | 'error' | 'idle';
    error?: unknown;
}

const InitialListStatus: ListStatus = {
    status: 'idle',
    error: undefined,
};

export interface RisrSort {
    id: string;
    name: string;
    sort: {
        sort_on: string;
        sort_order: "asc" | "desc";
    }
}

interface ConfigResponse {
    filters: RisrExpandableSchemaField[];
    sorts: RisrSort[]
}

export interface ListSource<T extends { id: string }> {
    search(query: ListSearch): Promise<ListData<T>>;
    config(query: Record<string, any>): Promise<ConfigResponse>;
}

export interface ListOptions {
    featuredFilters?: string[];
    fixedFilter?: Record<string, any>;
    allowedFilters?: string[];
}

/**
 * Factory function that creates a ListSource for searching and paginating API resources.
 *
 * @template T - The domain model type that will be returned, must have an 'id' property
 * @template K - The API response item type
 * @param path - The API endpoint path for search requests
 * @param params - Configuration object containing:
 *   - api: The API client for making requests
 *   - instantiate: Function to transform API response items into domain models
 * @returns A ListSource object with a search method for querying the API
 */
export function searchSourceFactory<T extends { id: string }, K>(
    path: string,
    params: {
        api: ApiRaw,
        instantiate: (item: K) => T
    }
): ListSource<T> {
    return {
        search: async (query: ListSearch) => {
            const { api, instantiate } = params;
            const apiSearch: ListApiSearchRequest = {
                filter: query.filter,
                sort: query.sort,
                pagination: {
                    start: query.pagination.page * query.pagination.pageSize,
                    size: query.pagination.pageSize
                }
            };

            const response = await api.post<ListApiSearchResponse<K>>(path, apiSearch);
            return {
                items: response.docs.map(item => instantiate(item)),
                meta: {
                    totalItems: response.total,
                }
            };
        },
        config: async (context: Record<string, any>) => {
            const api = params.api;
            const response = await api.post<ConfigResponse>(`${path}/config`, context);
            return response;
        }
    }
}


@Injectable()
export class RisrList<T extends { id: string }> {
    private options = signal<ListOptions>({});
    private initialized = signal(false);
    private source?: ListSource<T>;
    private location = inject(Location);
    private baseHref = inject(APP_BASE_HREF);

    private status = signal<ListStatus>(InitialListStatus);
    private pagination = signal<ListPagination | undefined>(undefined);
    private filter = signal<Record<string, any> | undefined>({});
    private sort = signal<SearchSort | undefined>({});
    private page = signal<ListData<T> | undefined>(undefined);

    private config = signal<ConfigResponse | undefined>(undefined);
    public availableFilters = computed(() => {
        const available = this.config()?.filters || [];
        const options = this.options();
        const allowed = options.allowedFilters;
        const filtered = allowed ? available.filter((field: any) => allowed.includes(field.id || field.name)) : available;
        const featuredFields = options.featuredFilters || [];
        return {
            featured: filtered.filter((field: any) => featuredFields.includes(field.id || field.name)),
            advanced: filtered.filter((field: any) => !featuredFields.includes(field.id || field.name))
        };
    })
    public availableSorts = computed(() => {
        return this.config()?.sorts || [];
    })

    private fixedFilter = computed(() => {
        return this.options().fixedFilter || {};
    });

    private mergedFilter = computed(() => {
        if (this.fixedFilter()) {
            return {
                ...this.filter(),
                ...this.fixedFilter()
            };
        }
        return this.filter();
    })

    private triggerLoad = new Subject<string>();
    private triggerInit = new Subject<void>();

    private searchQuery = computed(() => {
        const initialized = this.initialized();
        const pagination = this.pagination();
        const filter = this.filter();
        const sort = this.sort();
        if (!initialized || !pagination || !filter || !sort) return null;
        return JSON.stringify({ pagination, filter: this.mergedFilter() || {}, sort });
    });

    constructor() {
        this.triggerLoad.pipe(
            distinctUntilChanged()
        ).subscribe(() => this.loadPage());
        this.triggerInit.subscribe(() => this.asyncInit());

        effect(() => {
            const query = this.searchQuery();
            if (query) {
                this.triggerLoad.next(query);
            }
        });

        inject(DestroyRef).onDestroy(() => this.destroy());
    }

    public resource = {
        state: this.status.asReadonly(),
        pagination: this.pagination.asReadonly(),
        filter: this.filter.asReadonly(),
        sort: this.sort.asReadonly(),
        page: this.page.asReadonly(),
    }

    init(source: ListSource<T>, listOptions: ListOptions) {
        this.options.set(listOptions);
        this.source = source;
        this.triggerInit.next();
    }

    private async asyncInit() {
        await this.loadConfig();
        this.initialized.set(true);
    }

    private async loadConfig() {
        const config = await this.source?.config({});
        if (config) {
            this.config.set(config);
        }
    }

    private constructSearch(): ListSearch {
        return {
            pagination: this.pagination() || DEFAULT_LIST_PAGINATION,
            filter: this.mergedFilter() || {},
            sort: this.sort() || {}
        };
    }

    private updateRoute() {
        let params = new HttpParams();
        const pagination = this.pagination();
        if (pagination?.page !== undefined) {
            params = params.append('page', pagination.page.toString());
        }
        if (pagination?.pageSize !== undefined) {
            params = params.append('pageSize', pagination.pageSize.toString());
        }
        const sort = this.sort();
        if (sort?.sort_on !== undefined) {
            params = params.append('sort_on', sort.sort_on);
        }
        if (sort?.sort_order !== undefined) {
            params = params.append('sort_order', sort.sort_order);
        }
        params = RisrList.serializeFilter(this.filter(), params);

        let finalPath = `${location.pathname.replace(this.baseHref, '')}`;
        if (!finalPath.startsWith('/')) {
            finalPath = `/${finalPath}`;
        }
        this.location.replaceState(finalPath, params.toString());
    }

    private async loadPage() {
        if (!this.source) {
            throw new Error('List source is not initialized');
        }
        logger.info('Loading page');
        const query = this.constructSearch();
        this.status.set({
            status: 'loading',
            error: undefined,
        });
        try {
            const data = await this.source.search(query);
            this.status.set({
                status: 'resolved',
                error: undefined,
            });
            this.page.set(data);
            this.updateRoute();
        } catch (error) {
            const err = toError(error);
            logger.error('Failed to load list page', err);
            this.status.set({
                status: 'error',
                error: err.message,
            });
        }
    }

    reload() {
        const query = this.searchQuery();
        if (query) {
            this.triggerLoad.next(query + ':reload');
        }
    }

    updatePagination(pagination: ListPagination) {
        this.pagination.set(pagination);
    }

    updateFilter(filter: Record<string, any>) {
        this.filter.set(filter);
    }

    updateSort(sort: SearchSort) {
        this.sort.set(sort);
    }

    reset() {
        this.status.set(InitialListStatus);
        this.pagination.set(undefined);
        this.filter.set(undefined);
        this.sort.set(undefined);
        this.page.set(undefined);
    }

    destroy() {
        this.triggerInit.complete();
        this.triggerLoad.complete();
    }

    static serializeFilter(filter: Record<string, any> | undefined, params: HttpParams): HttpParams {
        if (!filter) return params;
        for (const [key, value] of Object.entries(filter)) {
            if (value === null || value === undefined || value === '') continue;
            if (Array.isArray(value)) {
                if (value.length > 0) {
                    params = params.append(`f.${key}[]`, value.join(','));
                }
            } else {
                params = params.append(`f.${key}`, String(value));
            }
        }
        return params;
    }

    static parseFilterParams(keys: string[], getParam: (key: string) => string | null): Record<string, any> {
        const filter: Record<string, any> = {};
        keys
            .filter(key => key.startsWith('f.'))
            .forEach(key => {
                const value = getParam(key);
                if (value === null) return;
                if (key.endsWith('[]')) {
                    const name = key.substring(2, key.length - 2);
                    filter[name] = value.split(',');
                } else {
                    const name = key.substring(2);
                    filter[name] = value;
                }
            });
        return filter;
    }
}